import React, { useState, useMemo } from 'react';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import '../user-page-academic.css';
import {
  StudentProgramsWithCourses,
  CourseWithLecturesEntity,
} from '../../../../api/types/students/programs/student-programs-with-courses';
import { CourseWithAbstractDetails } from '../../../../api/types/students/courses/student-courses';
import { ViewToggle } from './components/view-toggle';
import { ProgramItem } from './components/program-item/program-item';
import { GradeDialog } from './components/grade-dialog/grade-dialog';
import { StatusDialog } from './components/status-dialog';
import { ProgramStudentStatus } from '../../../../api/types/programs/students/program-student-status';
import { ProgramWithStudentStatus, CourseWithGrades } from './types';
import { UserSummaryType } from '../../../../api/types/user/user-summary.type';
import { calculateAttendanceData } from './operations/calculate-attendance-data-operation';
import { calculateProgramAttendancePercentage } from './operations/calculate-program-attendance-operation';

// Grade component type
interface GradeComponent {
  id: number;
  name: string;
  weight: number;
  score?: number;
  type: 'assignment' | 'exam';
}

interface UserPageAcademicProps {
  programs: StudentProgramsWithCourses[];

  user: UserSummaryType;
  loadStudentDetails: () => any;
}

export const UserPageAcademic: React.FC<UserPageAcademicProps> = ({
  programs,
  user,
  loadStudentDetails,
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [expandedPrograms, setExpandedPrograms] = useState<
    Record<number, boolean>
  >({});
  const [expandedGradeDetails, setExpandedGradeDetails] = useState<
    Record<string, boolean>
  >({});
  const [gradeDialogVisible, setGradeDialogVisible] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<CourseWithGrades | null>(
    null,
  );
  const [statusDialogVisible, setStatusDialogVisible] = useState(false);
  const [selectedProgram, setSelectedProgram] =
    useState<ProgramWithStudentStatus | null>(null);
  const [newStatus, setNewStatus] = useState<ProgramStudentStatus>(
    ProgramStudentStatus.STUDYING,
  );
  const [statusDate, setStatusDate] = useState<Date | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<'program' | 'student'>(
    'student',
  );

  // Transform the program data to include computed fields
  const programsData = useMemo(() => {
    return (programs || []).map((program) => {
      const programAttendancePercentage =
        calculateProgramAttendancePercentage(program);

      return {
        ...program,
        currentYear: Math.ceil(
          (new Date().getTime() -
            new Date(
              program.studentStatus.studyingStartDate || new Date(),
            ).getTime()) /
            (1000 * 60 * 60 * 24 * 365),
        ),
        programName: `${program.programName}`, // TODO: Get actual program name from API
        programAttendancePercentage,
        courses: program.courses.map((course) => {
          const attendanceData = calculateAttendanceData(course);
          return {
            ...course,
            absoluteGrade: 90, // TODO: Get actual grades
            relativeGrade: 85,
            finalScore: course.finalGrade,
            gradeComponents: [], // TODO: Get actual grade components
            attendanceCount: attendanceData.attendanceCount,
            attendancePercentage:
              course.attendancePercentage /*attendanceData.attendancePercentage*/,
          };
        }),
      };
    });
  }, [programs]);

  // Toggle program expansion
  const toggleProgramExpand = (programId: number) => {
    setExpandedPrograms((prev) => ({
      ...prev,
      [programId]: !prev[programId],
    }));
  };

  // Check if program is expanded
  const isProgramExpanded = (programId: number) => {
    return !!expandedPrograms[programId];
  };

  // Check if grade details are expanded
  const isGradeDetailsExpanded = (programId: number, courseId: number) => {
    const key = `${programId}-${courseId}`;
    return !!expandedGradeDetails[key];
  };

  const openStatusDialog = (program: ProgramWithStudentStatus) => {
    setSelectedProgram(program);
    setNewStatus(program.studentStatus.status);
    setStatusDate(null);
    setStatusDialogVisible(true);
  };

  // Open grade breakdown dialog
  const openGradeDialog = (course: CourseWithGrades) => {
    setSelectedCourse(course);
    setGradeDialogVisible(true);
  };

  return (
    <div className="user-page-academic">
      <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
      <div className="programs-list">
        {programsData.map((program) => (
          <ProgramItem
            key={program.id}
            program={program}
            viewMode={viewMode}
            isProgramExpanded={isProgramExpanded}
            toggleProgramExpand={toggleProgramExpand}
            openGradeDialog={openGradeDialog}
            isGradeDetailsExpanded={isGradeDetailsExpanded}
            setExpandedGradeDetails={setExpandedGradeDetails}
            openStatusDialog={openStatusDialog}
            studentName={`${user.firstName} ${user.lastName}`}
            studentId={user.nationalId}
            studentGender={user.gender}
          />
        ))}
      </div>
      <GradeDialog
        visible={gradeDialogVisible}
        onHide={() => setGradeDialogVisible(false)}
        selectedCourse={selectedCourse}
      />
      <StatusDialog
        visible={statusDialogVisible}
        onHide={() => {
          setStatusDialogVisible(false);
          loadStudentDetails();
        }}
        selectedProgram={selectedProgram}
        user={user}
        newStatus={newStatus}
        setNewStatus={setNewStatus}
        statusDate={statusDate}
        setStatusDate={setStatusDate}
        setSelectedStatus={setSelectedStatus}
      />
    </div>
  );
};
